import { Link } from "@tanstack/react-router";
import { Edit2, Lock, Languages, Clock, LogOut, ChevronRight, ChevronDown } from "lucide-react";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useTranslation } from "react-i18next";
import Container from "@/components/ui/Container";
import { useQuery } from "@tanstack/react-query";
import { getLanguages } from "@/api/language.api";
import { useState, useEffect } from "react";
import { Select, MenuItem, FormControl } from "@mui/material";

export function ProfileDashboard({ onOpenPlans }: { onOpenPlans?: () => void }) {
    const { user, logout } = useAuth();
    const { t, i18n } = useTranslation();

    const getInitials = () => {
        if (user?.first_name && user?.last_name) {
            return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
        }
        return user?.username?.slice(0, 2).toUpperCase() || user?.email?.[0]?.toUpperCase() || "U";
    };

    // --- Language Logic (Same as Header) ---
    const { data: languages } = useQuery({
        queryKey: ["languages", i18n.resolvedLanguage],
        queryFn: () => getLanguages(),
        staleTime: 0,
    });

    const [selectedLang, setSelectedLang] = useState<any>(null);

    // Sync selectedLang with i18n's current language when languages are loaded
    useEffect(() => {
        if (languages && languages.length > 0) {
            const currentLangCode = i18n.language || i18n.resolvedLanguage;
            let matchedLang = languages.find(l => l.iso_code === currentLangCode);

            // If no match found (e.g., first time user), use first language
            if (!matchedLang) {
                matchedLang = languages[0];
                i18n.changeLanguage(matchedLang.iso_code);
            }

            if (!selectedLang || selectedLang.code !== matchedLang.code) {
                setSelectedLang(matchedLang);
            }
        }
    }, [languages, i18n.language]);

    const handleLanguageChange = (event: any) => {
        const langCode = event.target.value;
        const lang = languages?.find(l => l.code === langCode);
        if (lang) {
            setSelectedLang(lang);
            i18n.changeLanguage(lang.iso_code);
        }
    };
    // ---------------------------------------

    // ---------------------------------------

    type MenuItem = {
        id?: string;
        icon: any;
        label: string;
        to?: string;
        action?: (() => void) | undefined; // Allow undefined in type
    };

    const menuItems: MenuItem[] = [
        {
            icon: Edit2,
            label: "Edit Profile",
            to: "/profile/edit",
        },
        {
            icon: Lock,
            label: t('auth.changePassword.title') || "Change Password",
            to: "/profile/change-password",
        },
        {
            id: 'language',
            icon: Languages,
            label: "Language",
            // Custom render handling below
        },
        {
            icon: Clock,
            label: t('header.nav.plan') || "Plan History",
            action: onOpenPlans,
        },
        {
            icon: LogOut,
            label: t('header.auth.logout') || "Log Out",
            action: logout,
        }
    ];

    return (
        <div className="bg-[#FFF8F3] min-h-screen py-12">
            <Container>
                <div className="flex flex-col md:flex-row gap-12 justify-center items-start">

                    {/* User Info / Sidebar */}
                    <div className="flex flex-col items-center gap-4 w-full md:w-64">
                        <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-sm flex items-center justify-center bg-[#187A85] text-white text-4xl font-bold">
                            {user?.profile_photo_url ? (
                                <img src={user.profile_photo_url} alt="Profile" className="w-full h-full object-cover" />
                            ) : (
                                getInitials()
                            )}
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 text-center">
                            {user?.first_name ? `${user.first_name} ${user.last_name}` : user?.username || "Traveler"}
                        </h2>
                    </div>

                    {/* Menu Options */}
                    <div className="flex-1 max-w-xl w-full">
                        <h1 className="text-2xl font-bold text-[#187A85] mb-8 text-center md:text-left">
                            My Profile
                        </h1>

                        <div className="flex flex-col gap-4">
                            {menuItems.map((item, index) => {
                                // Common Icon Container
                                const IconContainer = (
                                    <div className="p-2.5 rounded-xl bg-orange-50 text-[#e55a28] group-hover:bg-[#e55a28] group-hover:text-white transition-colors">
                                        <item.icon size={20} />
                                    </div>
                                );

                                // Special case for Language to embed Select
                                if (item.id === 'language') {
                                    return (
                                        <div key={index} className="bg-white p-2 rounded-2xl shadow-sm hover:shadow-md transition-shadow flex items-center justify-between cursor-pointer group relative">
                                            {/* Wrapper to control layout */}
                                            <div className="flex items-center gap-4 w-full px-3">
                                                {IconContainer}

                                                <FormControl fullWidth variant="standard">
                                                    <Select
                                                        value={selectedLang?.code || ''}
                                                        onChange={handleLanguageChange}
                                                        disableUnderline
                                                        displayEmpty
                                                        IconComponent={() => <ChevronRight className="text-gray-400 group-hover:text-[#187A85] transition-colors" />}
                                                        renderValue={(selected) => {
                                                            const lang = languages?.find(l => l.code === selected);
                                                            return (
                                                                <div className="flex items-center justify-between w-full">
                                                                    <span className="font-medium text-gray-700">{item.label}</span>
                                                                    {lang && (
                                                                        <div className="flex items-center gap-2 mr-2">
                                                                            <span className="text-xl">{lang.flag}</span>
                                                                            <span className="text-slate-500 font-normal">{lang.iso_code.toUpperCase()}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        }}
                                                        sx={{
                                                            py: 1.5,
                                                            '& .MuiSelect-select': {
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                pr: '0 !important' // Remove default padding for standard arrow
                                                            }
                                                        }}
                                                        MenuProps={{
                                                            disableScrollLock: true
                                                        }}
                                                    >
                                                        {languages?.map((lang) => (
                                                            <MenuItem key={lang.code} value={lang.code}>
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-lg">{lang.flag}</span>
                                                                    <span>{lang.name}</span>
                                                                </div>
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </div>
                                        </div>
                                    )
                                }

                                const Content = (
                                    <div className="bg-white p-5 rounded-2xl shadow-sm hover:shadow-md transition-shadow flex items-center justify-between cursor-pointer group">
                                        <div className="flex items-center gap-4">
                                            {IconContainer}
                                            <span className="font-medium text-gray-700">{item.label}</span>
                                        </div>
                                        <ChevronRight className="text-gray-400 group-hover:text-[#187A85] transition-colors" />
                                    </div>
                                );

                                if (item.action) {
                                    return (
                                        <div key={index} onClick={() => item.action && item.action()}>
                                            {Content}
                                        </div>
                                    );
                                }

                                if (item.to) {
                                    return (
                                        <Link key={index} to={item.to} className="block">
                                            {Content}
                                        </Link>
                                    );
                                }

                                return <div key={index}>{Content}</div>
                            })}
                        </div>

                        <div className="mt-12 text-center">
                            <button className="text-red-500 font-medium hover:text-red-700 transition-colors">
                                Delete Account
                            </button>
                        </div>
                    </div>

                </div>
            </Container>
        </div>
    );
}
